import useAxios from "src/core/shared/hooks/useAxios";
import {
  RequestConfig,
  UseApiRequest,
  EndpointConfig
} from "src/core/shared/api/config.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

type ImportSettingEndpointConfig = {
  GET_IMPORT_SETTING: EndpointConfig<
    {
      type: ImportSettingType;
      origin: ImportOrigin;
    },
    IImportSetting
  >;
  TRIGGER_FTP_IMPORT: EndpointConfig<
  number,
  { message: string; }
>
};

/**
 * Import setting API
 */
const requests: RequestConfig<ImportSettingEndpointConfig> = {
  GET_IMPORT_SETTING: params => ({
    url: `${process.env.REACT_APP_API_URL}/public/import-settings/type`,
    method: "GET",
    headers: false,
    params,
    cache: {
      maxAge: cacheMinutesToMilliseconds(15),
      clearOnError: true
    }
  }),
  TRIGGER_FTP_IMPORT: importSettingId => ({
    url: `${process.env.REACT_APP_API_URL}/core/import-settings/${importSettingId}/trigger-ftp`,
    method: "POST"
  })
};

export enum ImportOrigin {
  CSV = "csv",
  API = "api",
  SFTP = "ftp",
  FORM = "form"
}

export interface IImportSetting<D extends Record<string, any> = Record<string, any>, Options extends ImportSettingOptions = ImportSettingOptions> {
  id: number;
  name: string;
  type: ImportSettingType;
  origin: ImportOrigin;
  options?: Options;
  headerMappings: Array<ImportFieldMapping<D>>
}

export interface ImportSettingOptions {
  file?: FileNameOptions;
  /** The grouping for inputs related header mappings */
  formFieldGroups?: Record<string, string[]>
}

export interface FileNameOptions {
  name: string;
  template: string;
  dateFormat?: string;
}

export enum ImportSettingType {
  Product = "products",
  Customer = "customers",
  Price = "prices",
  User = "users",
  Nutrition = "nutrition",
  ProductApproval = "product-approval",
  PendingCustomer = "pending-customer"
}

export type ImportType = "product" | "customer" | "price" | "pending-customer";

export type ImportFieldValueType = "text" | "password" | "date" | "time" | "number" | "email" | "phone";

export interface ImportFieldMapping<ImportJSON> {
  /** The CSV column header */
  external: string;
  /** Internal CSV header to normalise data */
  internal: keyof ImportJSON;
  /** If no value then default to this */
  defaultValue?: string;
  /** Date format e.g YYYY-MM-DD */
  dateFormat?: string;
  /** Flags that this is a custom field */
  custom?: boolean;
  /** Long description to map custom fields to */
  description?: string;
  /** For custom fields determins if the value should be visible to customers or admins only */
  admin?: boolean;
  /** For a custom field should we add an audit log for this change (NOTE: only for order status import) */
  audit?: boolean;
  /** For list of available options used for forms */
  options?: string[];
  /** The value type */
  type?: ImportFieldValueType;
  /** Form field component */
  formFieldType?: "input" | "textarea" | "select" | "radio" | "checkbox" | "range";
  /** validation options */
  validation?: Validation;
}

export interface Validation {
  required?: boolean;
  regex?: string;
  regexMessage?: string;
  min?: number;
  max?: number;
}

export interface PendingCustomerJSON {
  ContactName: string;
  CustomerName: string;
  Website?: string;
  AddressLine1: string;
  AddressLine2?: string;
  AddressLine3?: string;
  City?: string;
  PostCode: string;
  PhoneNumber?: string;
  ContactNumber: string;
  ContactEmail: string;
  TypeOfBusiness: string;
  NumberOfLocations: string;
  CurrentSupplier?: string;
  HeardFromSource?: string;
  AdditionalComments?: string;
}

export const useImportSettingApi: UseApiRequest<ImportSettingEndpointConfig> = name => useAxios(requests[ name ], name);
